import { BfxBlotterOrdersContextMenuItemDetailFillState } from "./BfxBlotterOrdersContextMenuItemDetailFillState";

import { BfxBlotterOrdersContextMenuState } from "./BfxBlotterOrdersContextMenuState";

import { BfxBlotterAction } from "../bfx-base-blotter/BfxBlotterAction";

import { ContextMenuItemState } from "../ContextMenuItemState";
import { ContextMenuItemStateCommand } from "../ContextMenuItemStateCommand";
import { ContextMenuItemStateCommandCriteria } from "../ContextMenuItemStateCommandCriteria";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { OrderRelationshipType } from "shared/cubiq-model-multi-asset/model/enum/OrderRelationshipType";
import { AbstractFillMenuItem } from "./AbstractFillMenuItem";

export class BfxBlotterOrdersContextMenuDetailFillItem extends AbstractFillMenuItem {
  constructor() {
    super(
      BfxBlotterAction.ACTION_ORDER_DETAILFILL,
      "Detail Fill",
      BfxBlotterOrdersContextMenuDetailFillItem.checkAvailableFn(),
      BfxBlotterOrdersContextMenuDetailFillItem.checkEnabledFn(),
      ContextMenuItemStateCommandCriteria.MenuItem,
      ContextMenuItemStateCommandCriteria.Item,
    );
  }

  private static checkAvailableFn(): ContextMenuItemStateCommand<CubiqTS.OrderDataImpl> {
    return function (
      this: void,
      menuState: BfxBlotterOrdersContextMenuState,
      itemState: ContextMenuItemState,
    ) {
      itemState.available = menuState.canFillOrders;
    };
  }

  /**
   * note- itemState.enabled startes off true for QuickFill
   * this fuction may set it false but must not set it true
   */
  private static checkEnabledFn(): ContextMenuItemStateCommand<CubiqTS.OrderDataImpl> {
    return function (
      this: void,
      menuState: BfxBlotterOrdersContextMenuState,
      itemState: BfxBlotterOrdersContextMenuItemDetailFillState,
      item: CubiqTS.OrderDataImpl,
    ) {
      if (!item || !itemState.enabled || !itemState.available) {
        // ineligible item encountered, enabled will never be true
        return;
      }

      itemState.enabled = false == AbstractFillMenuItem.isBothSidesOfGamma(item, itemState);

      // If two siblings of an OCO are selected quick fill should be disabled as per OMS-21024
      if (
        item.siblingRelationship != null &&
        item.siblingRelationship.relationshipType === OrderRelationshipType.OCO
      ) {
        let orderId: string = item.orderId.toString();
        let relatedOrderId: string = item.siblingRelationship.orderId;

        if (itemState.relatedOrders[relatedOrderId] != null) {
          // A OCO sibling is selected, disable the quick fill
          itemState.enabled = false;
        } else {
          itemState.relatedOrders[orderId] = relatedOrderId;
        }
      }

      // Order is locked
      if (
        item.lockType !== null &&
        (item.lockType === CubiqTS.LockType.FILL_LOCK ||
          item.lockType === CubiqTS.LockType.FILL_NOW_LOCK ||
          item.lockType === CubiqTS.LockType.MODIFY_LOCK ||
          item.lockType === CubiqTS.LockType.SI_LOCK ||
          item.lockType === CubiqTS.LockType.CANCEL_LOCK ||
          item.lockType === CubiqTS.LockType.TRIGGER_LOCK)
      ) {
        itemState.enabled = false;
      }

      if (
        menuState.activeUser.isTrader() &&
        item.deskId != null &&
        !menuState.activeUser.tradingGroupsIdSet.has(item.deskId.toString())
      ) {
        itemState.enabled = false;
      }

      if (
        menuState.activeUser.isSales() &&
        item.salesGroupId &&
        !menuState.activeUser.salesGroupsIdSet.has(item.salesGroupId.toString())
      ) {
        itemState.enabled = false;
      }

      // Order state:   in intervention, or suspended
      switch (item.orderState) {
        case CubiqTS.OrderState.PENDING:
        case CubiqTS.OrderState.PENDING_CANCELLATION:
        // intervention
        case CubiqTS.OrderState.ACCEPTANCE_NEEDED: // Modify in Pending, New Pending
        case CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED: // Modify Out Pending
        case CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED: // Cancel Pending
        case CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED: // Migration Acceptance Needed
        // suspended
        case CubiqTS.OrderState.SUSPENDED: // Suspended
        case CubiqTS.OrderState.FILL_PENDING:
        case CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.EXT_CANCEL_ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.EXPIRY_PENDING_FWDS:
        case CubiqTS.OrderState.CANCEL_PENDING_FWDS:
        case CubiqTS.OrderState.CANCELLED:
        case CubiqTS.OrderState.FILLED:
        case CubiqTS.OrderState.REJECTED:
        case CubiqTS.OrderState.EXPIRED:
          itemState.enabled = false;
          break;
      }

      // no detail fill for paused orders
      if (
        CubiqTS.PauseState.PAUSED === item.pauseState ||
        CubiqTS.PauseState.PAUSE_PENDING === item.pauseState
      ) {
        itemState.enabled = false;
      }

      // item is with external trader group
      if (item.deskId != null) {
        let tradingGroup = menuState.traderGroups.get(item.deskId.toString());

        if (tradingGroup && tradingGroup.isExternallyFilledFg) {
          itemState.enabled = false;
        }
      }

      if (itemState.enabled) {
        //   itemState.count++;

        // item is in transfer
        if (item.targetDeskId !== undefined && item.targetDeskId !== null) {
          itemState.warnings.show = true;
          itemState.warnings.transfer.display = true;
          itemState.warnings.transfer.ids.push(item.orderId.toString());
          itemState.warnings.transfer.orderRefs.push(item.orderRef);
        }
        // order is about to expire
        if (
          item.orderState === CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED ||
          item.orderState === CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED
        ) {
          itemState.warnings.show = true;
          itemState.warnings.expired.display = true;
          itemState.warnings.expired.ids.push(item.orderId.toString());
          itemState.warnings.expired.orderRefs.push(item.orderRef);
        }
        // order credit failed and waiting approval in DI
        if (
          item.creditRevalDetails instanceof CubiqTS.CreditRevalDetailsImpl &&
          item.creditRevalDetails != null &&
          item.creditRevalDetails !== undefined &&
          (item.creditRevalDetails as CubiqTS.CreditRevalDetailsImpl).lastCreditStatus >= 0
        ) {
          if (
            CubiqTS.CreditStatus.CREDIT_FAILED ===
              (item.creditRevalDetails as CubiqTS.CreditRevalDetailsImpl).lastCreditStatus ||
            CubiqTS.CreditStatus.CREDIT_TIMEDOUT ===
              (item.creditRevalDetails as CubiqTS.CreditRevalDetailsImpl).lastCreditStatus
          ) {
            itemState.warnings.show = true;
            itemState.warnings.credit_check.display = true;
            itemState.warnings.credit_check.ids.push(item.orderId.toString());
            itemState.warnings.credit_check.orderRefs.push(item.orderRef);
          }
        }
      }
    };
  }

  public static get state(): any {
    return new BfxBlotterOrdersContextMenuItemDetailFillState();
  }
}
